import React, { Component } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { Toast } from 'primereact/toast';
import Authentication from '../../../session';
import LoadingComponent from '../../../loadingComponent';
import withRouter from '../../../lib/withRouter';
import { getBranchesLatest } from '../../../../store/actions';
import { INITIAL_STATE, TOAST_TYPES, APPROVAL_STATUS, ADJUSTMENT_TYPES } from './constants';
import { StudentAdmissionChangeRequestsService } from './services';
import { StudentAdmissionChangeUtils } from './utils';
import { FeeHandlers } from './utils/feeHandlers';
import SearchFilters from './components/SearchFilters';
import StudentsDataTable from './components/StudentsDataTable';
import AdmissionChangeRequestDialog from './components/AdmissionChangeRequestDialog';
import RejectTransferDialog from './components/RejectTransferDialog';
import ApprovalRequestsDialog from './components/ApprovalRequestsDialog';
import OldFeeInfoDialog from './components/OldFeeInfoDialog';
import NewFeeInfoDialog from './components/NewFeeInfoDialog';
import './styles.scss';

class StudentAdmissionChangeRequests extends Component {
    constructor(props) {
        super(props);
        this.state = { 
            ...INITIAL_STATE,
            filterbranchId: this.props.branchData?.find((e) => e.key === this.props.branchData[0]?.key)?.key
        };

        // Bind methods
        this.debouncedApiCall = debounce(this.filterUsersData, 500);
        this.service = new StudentAdmissionChangeRequestsService();
        this.feeHandlers = new FeeHandlers(this);
    }

    // Lifecycle Methods
    componentDidMount() {
        if (this.state?.dropdownsData?.sectionId) {
            this.filterUsersData();
        }
    }

    // Utility Methods
    updateState = (updates, callback) => {
        this.setState(updates, callback);
    };

    showToast = (severity, summary, detail = '', life = 3000) => {
        this.toast?.show({ severity, summary, detail, life });
    };

    // Event Handlers
    onSearchClick = (filterData) => {
        this.updateState({ filterData }, this.filterUsersData);
    };

    onPage = (event) => {
        this.updateState(prevState => ({
            lazyParams: { ...prevState.lazyParams, ...event }
        }), this.filterUsersData);
    };

    onSort = (event) => {
        this.updateState(prevState => ({
            lazyParams: { ...prevState.lazyParams, ...event }
        }), this.filterUsersData);
    };

    onFilter = (event) => {
        event['first'] = 0;
        this.updateState(prevState => ({
            lazyParams: {
                ...prevState.lazyParams,
                filters: event.filters
            }
        }), this.filterUsersData);
    };

    onGlobalFilterChange = (e) => {
        const value = e.target.value;
        const filters = { ...this.state.filters };
        filters['global'].value = value;
        this.updateState({ filters, globalFilterValue: value });
    };

    onSelectionChange = (e) => {
        this.updateState({ selectedRows: e.value });
    };

    // Search and Level Handlers
    handleSetSearchTypeValue = (value) => {
        this.updateState({
            searchType: value,
            isUserNameSearch: value === 'studentName',
            isUserEnrollSearch: value !== 'studentName'
        });
    };

    handleSetSearchValue = (value) => {
        this.updateState({ searchValue: value }, () => {
            this.debouncedApiCall(this.state.searchValue);
        });
    };

    handleSearchLevel = (value) => {
        this.updateState({ searchLevel: value }, this.filterUsersData);
    };

    handleFocus = () => {
        if (this.state.searchLevel === 3) {
            this.updateState({ focused: true });
        }
    };

    handleBlur = () => {
        if (this.state.searchLevel === 3) {
            this.updateState({ focused: false });
        }
    };

    // Approval and Rejection Handlers
    rejectedData = () => {
        this.updateState(prevState => ({
            isRejectTransferDialogOpen: true,
            rowData: {
                ...prevState.rowData,
                toTransferStatus: this.state?.flagForTransfer
            }
        }), () => {
            if (this.state?.isRejectTransferDialogOpen) {
                this.updateTransfer(APPROVAL_STATUS.REJECT);
            }
        });
    };

    acceptedData = () => {
        this.updateState(prevState => ({
            rowData: {
                ...prevState.rowData,
                toTransferStatus: this.state?.flagForTransfer
            }
        }), () => {
            this.updateTransfer(APPROVAL_STATUS.APPROVE);
        });
    };

    updateTransfer = async (approveStatus) => {
        if (!this.state?.rowData || !Object.keys(this.state?.rowData).length) {
            this.updateState({ isLoading: false });
            this.showToast(TOAST_TYPES.ERROR, 'Assign Records shouldn\'t not empty', 'Please select At least one record');
            return;
        }

        this.updateState({ isLoading: true });

        const result = await this.service.updateAdmissionChange(
            this.state.rowData,
            approveStatus,
            this.state.dropdownsData,
            this.state.advanceAdjustmentType,
            this.state.isManualAdjustment
        );

        if (result.success) {
            this.updateState({
                isDialogOpenForApproveStudent: false,
                isRejectTransferDialogOpen: false,
                rowData: {},
                isShowInstallmentInfo: false,
                selectedFeePlan: null,
                selectedFeeStructure: [],
                installmentData: [],
                oneTimeFees: [],
                newFeeResponse: {},
                oldFeeInfo: [],
                onShowOldFeeInfo: false,
                onShowNewFeeInfo: false,
                isLoading: false,
                advanceAdjustmentType: ADJUSTMENT_TYPES.SYSTEM,
                isManualAdjustment: false
            });

            this.showToast(TOAST_TYPES.SUCCESS, `Successfully ${this.state?.flagForTransfer}`, result.message);
            this.filterUsersData();
        } else {
            this.updateState({ isLoading: false });
            this.showToast(TOAST_TYPES.ERROR, 'Some error occurred', result.error);
        }
    };

    // Fee and Dialog Handlers
    onViewModifiedAmount = async () => {
        const { rowData, effectiveDate } = this.state;
        
        const result = await this.service.getModifiedAmount(rowData, effectiveDate);
        
        if (result.success) {
            const mergedOldFeeInfo = StudentAdmissionChangeUtils.mergeOldFeeInfo(result.response, rowData);
            const totalClosingDueAmt = mergedOldFeeInfo.feeInfo.reduce((sum, item) => sum + (item?.closingDueAmt || 0), 0);
            const totalClosingAdvance = mergedOldFeeInfo.feeInfo.reduce((sum, item) => sum + (item?.closingAdvance || 0), 0);

            this.updateState({
                oldFeeInfo: mergedOldFeeInfo || [],
                newFeeResponse: result.data?.newFeeResponse || {},
                manualAdjustmentFeeInfo: result.data?.manualAdjustmentFeeInfo || {},
                showResponseDialog: false,
                onShowOldFeeInfo: this.state.rowData?.admissionRule !== 5,
                onShowNewFeeInfo: this.state.rowData?.admissionRule === 5,
                totalClosingDueAmt,
                totalClosingAdvance,
                rowData: {
                    ...this.state.rowData,
                    oldFeeInfo: mergedOldFeeInfo?.feeInfo
                }
            }, () => {
                if (this.state.rowData?.admissionRule === 5) {
                    this.feeHandlers.handleFeePlanChange();
                }
            });
        } else if (!result.success && result.data) {
            this.updateState({
                oldFeeInfo: result.data?.oldFeeInfo || [],
                newFeeResponse: result.data?.newFeeResponse || {},
                manualAdjustmentFeeInfo: result.data?.manualAdjustmentFeeInfo || {},
                showResponseDialog: false,
                onShowOldFeeInfo: false,
                onShowNewFeeInfo: true,
            }, () => {
                this.feeHandlers.handleFeePlanChange();
            });
        } else {
            this.updateState({
                selectedAdmission: '',
                effectiveDate: '',
                remarks: '',
            });
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error || 'Failed to fetch modified amount');
        }
    };

    getFeeStructure = async () => {
        if (this.state.rowData?.user_info?.branchId) {
            this.updateState({ isLoading: true });
            
            const result = await this.service.getFeeStructure(this.state.rowData?.user_info?.branchId);
            
            if (result.success) {
                this.updateState({
                    feeStructureInfo: result.data,
                    isLoading: false
                });
            } else {
                this.showToast(TOAST_TYPES.ERROR, 'Error', 'An error occurred while Getting Fee Category');
                this.updateState({ feeStructureInfo: [], isLoading: false });
            }
        }
    };

    onHandleRaiseChangeRequest = async (rowData) => {
        this.updateState({ isLoading: true });

        const result = await this.service.checkAdmissionChangeEligibility(rowData, this.state.effectiveDate);
        
        this.updateState({ isLoading: false });

        if (result.success) {
            if (result.data?.length !== 0) {
                this.updateState({
                    admissionChangeCheckResponse: result.data,
                    message: result.message,
                    showResponseDialog: true,
                    isDialogOpenForApproveStudent: false,
                });
            } else {
                this.updateState({
                    admissionChangeCheckResponse: [],
                    message: result.message,
                    isDialogOpenForApproveStudent: false,
                    showResponseDialog: false,
                }, () => this.onViewModifiedAmount());
            }
        } else {
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    onHandleSchedulerRefresh = async () => {
        this.updateState({ isLoading: true });

        const result = await this.service.updateSchedulerByDate();
        
        this.updateState({ isLoading: false });

        if (result.success) {
            this.showToast(TOAST_TYPES.SUCCESS, 'Success', result.message);
            this.filterUsersData();
        } else {
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    // Dialog Control Methods
    handleApproveClick = (rowData) => {
        this.updateState({
            isDialogOpenForApproveStudent: true,
            rowData: rowData,
            branchId: rowData?.tobranchId,
            branchName: rowData?.tobranchName,
            flagForTransfer: 'APROVE',
            effectiveDate: rowData?.effectiveDate
        }, async () => await this.getFeeStructure());
    };

    handleRejectClick = (rowData) => {
        this.updateState({
            isRejectTransferDialogOpen: true,
            rowData: rowData,
            branchId: rowData?.tobranchId,
            branchName: rowData?.tobranchName,
            flagForTransfer: 'REJECT'
        });
    };

    handleAcceptRequest = () => {
        this.onHandleRaiseChangeRequest(this.state?.rowData);
    };

    handleCancelRequest = () => {
        this.updateState({ isDialogOpenForApproveStudent: false });
    };

    handleRejectRequest = () => {
        this.rejectedData(this.state?.rowData);
    };

    handleCancelReject = () => {
        this.updateState({ isRejectTransferDialogOpen: false });
    };

    handleShowNewFeeInfo = () => {
        this.updateState({
            showResponseDialog: false,
            onShowOldFeeInfo: false,
            onShowNewFeeInfo: true,
        }, () => this.feeHandlers.handleFeePlanChange());
    };

    handleBackToOldFeeInfo = () => {
        this.updateState({
            onShowOldFeeInfo: this.state.oldFeeInfo?.length ? true : false,
            onShowNewFeeInfo: false,
            isRaiseDialogOpen: !this.state.oldFeeInfo?.length ? true : false,
            onShowOldFeeInfo: true
        });
    };

    handleBackToApprovalDialog = () => {
        this.updateState({
            onShowOldFeeInfo: false,
            isRaiseDialogOpen: true,
            advanceAdjustmentType: ADJUSTMENT_TYPES.SYSTEM,
            isManualAdjustment: false,
            isDialogOpenForApproveStudent: true
        });
    };

    handleAdjustmentTypeChange = (type, isManual) => {
        this.updateState({
            advanceAdjustmentType: type,
            isManualAdjustment: isManual
        });
    };

    handleApproveFeeSubmission = () => {
        this.updateState({
            showResponseDialog: false,
            onShowOldFeeInfo: false,
            onShowNewFeeInfo: true
        }, () => this.feeHandlers.handleFeeSubmission());
    };

    handleCancelAll = () => {
        this.updateState({
            showResponseDialog: false,
            remarks: "",
            effectiveDate: '',
            selectedAdmission: '',
            onShowOldFeeInfo: false,
            onShowNewFeeInfo: false,
            advanceAdjustmentType: ADJUSTMENT_TYPES.SYSTEM,
            isManualAdjustment: false
        });
    };

    render() {
        const { isLoading, changeRequests, totalRecords, lazyParams, filters, globalFilterValue } = this.state;
        const oldFeeInfo = this.state.oldFeeInfo?.feeInfo || [];
        const totalClosingDueAmt = oldFeeInfo.reduce((sum, item) => sum + (item.closingDueAmt || 0), 0);
        const totalClosingAdvance = oldFeeInfo.reduce((sum, item) => sum + (item.closingAdvance || 0), 0);

        return (
            <div>
                <SearchFilters
                    searchLevel={this.state.searchLevel}
                    onSearchLevelChange={this.handleSearchLevel}
                    onDropdownValuesChange={this.setDropdownValues}
                    onSchedulerRefresh={this.onHandleSchedulerRefresh}
                />

                <StudentsDataTable
                    changeRequests={changeRequests}
                    isLoading={isLoading}
                    lazyParams={lazyParams}
                    totalRecords={totalRecords}
                    filters={filters}
                    globalFilterValue={globalFilterValue}
                    selectedRows={this.state.selectedRows}
                    onPage={this.onPage}
                    onSort={this.onSort}
                    onFilter={this.onFilter}
                    onSelectionChange={this.onSelectionChange}
                    onGlobalFilterChange={this.onGlobalFilterChange}
                    onApprove={this.handleApproveClick}
                    onReject={this.handleRejectClick}
                />

                <AdmissionChangeRequestDialog
                    visible={this.state.isDialogOpenForApproveStudent}
                    onHide={this.handleCancelRequest}
                    rowData={this.state.rowData}
                    effectiveDate={this.state.effectiveDate}
                    onEffectiveDateChange={(e) => this.updateState({ effectiveDate: e.target.value })}
                    onAccept={this.handleAcceptRequest}
                    onCancel={this.handleCancelRequest}
                />

                <RejectTransferDialog
                    visible={this.state.isRejectTransferDialogOpen}
                    onHide={this.handleCancelReject}
                    rowData={this.state.rowData}
                    onReject={this.handleRejectRequest}
                    onCancel={this.handleCancelReject}
                />

                <ApprovalRequestsDialog
                    visible={this.state.showResponseDialog}
                    onHide={() => this.updateState({ showResponseDialog: false, admissionChangeCheckResponse: null, message: '' })}
                    admissionChangeCheckResponse={this.state.admissionChangeCheckResponse}
                    message={this.state.message}
                    onCancel={() => this.updateState({ showResponseDialog: false, remarks: "", effectiveDate: '', selectedAdmission: '' })}
                />

                <OldFeeInfoDialog
                    visible={this.state.oldFeeInfo && this.state.onShowOldFeeInfo && this.state.rowData?.admissionRule !== 5}
                    onHide={() => this.updateState({ showResponseDialog: false, admissionChangeCheckResponse: null, message: '', onShowOldFeeInfo: false, advanceAdjustmentType: ADJUSTMENT_TYPES.SYSTEM, isManualAdjustment: false })}
                    oldFeeInfo={this.state.oldFeeInfo}
                    totalClosingDueAmt={totalClosingDueAmt}
                    totalClosingAdvance={totalClosingAdvance}
                    advanceAdjustmentType={this.state.advanceAdjustmentType}
                    isManualAdjustment={this.state.isManualAdjustment}
                    onBack={this.handleBackToApprovalDialog}
                    onShowNewFeeInfo={this.handleShowNewFeeInfo}
                    onCancel={this.handleCancelAll}
                    onAdjustmentTypeChange={this.handleAdjustmentTypeChange}
                />

                <NewFeeInfoDialog
                    visible={this.state.onShowNewFeeInfo}
                    onHide={() => this.updateState({ onShowNewFeeInfo: false, advanceAdjustmentType: ADJUSTMENT_TYPES.SYSTEM, isManualAdjustment: false })}
                    selectedFeeStructure={this.state.selectedFeeStructure}
                    installmentData={this.state.installmentData}
                    oneTimeFees={this.state.oneTimeFees}
                    selectedFeePlan={this.state.selectedFeePlan}
                    isShowInstallmentInfo={this.state.isShowInstallmentInfo}
                    onBack={this.handleBackToOldFeeInfo}
                    onApprove={this.handleApproveFeeSubmission}
                    onCancel={this.handleCancelAll}
                    onPercentageChange={this.feeHandlers.handlePercentageChange}
                    onCalculatedFeeChange={this.feeHandlers.handleCalculatedFeeChange}
                    onOneTimeDueDateChange={(value, rowData) => {}}
                />

                <Toast ref={(el) => (this.toast = el)} position="bottom-right" />
                {isLoading && <LoadingComponent />}
            </div>
        );
    }

    // API Methods
    setDropdownValuesDilog = (dropdownsData) => {
        this.updateState(prevState => ({
            dropdownsDilogData: dropdownsData,
            rowData: {
                ...prevState.rowData,
                tosectionName: dropdownsData?.selectedsectionName,
                tosectionId: dropdownsData?.sectionId,
                toTransferStatus: this.state?.flagForTransfer
            }
        }));
    };

    setDropdownValues = (dropdownsData) => {
        this.updateState({ dropdownsData }, () => {
            // Check if branchIds is empty array and prevent API call
            if (!this.state?.dropdownsData?.branchIds ||
                (Array.isArray(this.state?.dropdownsData?.branchIds) && this.state?.dropdownsData?.branchIds.length === 0)) {
                console.log('No branches selected, skipping API call');
                this.updateState({
                    isLoading: false,
                    studentInfo: [],
                    studentstotalRecords: 0
                });
                return;
            }
            this.filterUsersData(dropdownsData);
        });
    };

    filterUsersData = async () => {
        this.updateState({ isLoading: true, changeRequests: [] });

        const { lazyParams, globalSearch, searchLevel, searchType, searchValue, dropdownsData } = this.state;

        const result = await this.service.getChangeRequests(
            lazyParams,
            globalSearch,
            dropdownsData,
            searchLevel,
            searchType,
            searchValue
        );

        if (result.success) {
            this.updateState({
                isLoading: false,
                changeRequests: result.data,
                totalRecords: result.totalRecords,
                isUserNameSearch: false,
                isUserEnrollSearch: false,
                searchValue: ''
            });
        } else {
            this.updateState({ isLoading: false });
            this.showToast(TOAST_TYPES.ERROR, 'Some error occurred', result.error);
        }
    };
}

const mapStatesToProps = (state) => ({
    branchData: state.branchDataLatest?.data?.data?.filter(branch => branch.level === 1) || []
});

export default connect(mapStatesToProps, {
    getBranchesLatest
})(Authentication(withRouter(StudentAdmissionChangeRequests)));
